import axios from '@/lib/axios'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { ToastInterface, useToast } from 'vue-toastification'

import { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'

import {
	CreateRatePlanCodeParams,
	RatePlanCode,
} from '@/internal/modules/providers/interfaces/RatePlanCode'

interface Props {
	immediate?: boolean
	params?: Params
	config?: Config
}

interface Config {
	errorHandling: boolean
}

interface Params {
	chainId?: string | number
}

const getRatePlanCodes = async (
	params: Params,
	errorHandling = true
): Promise<RatePlanCode[]> => {
	const { data } = await axios.get<RatePlanCode[]>(
		`api/internal/chains/${params.chainId}/rate-plan-codes`,
		{
			errorHandling,
		} as CustomAxiosRequestConfig
	)

	return data
}

const createRatePlanCode = async (
	code: CreateRatePlanCodeParams,
	toast: ToastInterface
) => {
	try {
		const { data } = await axios.post<RatePlanCode>(
			`/api/internal/chains/${code.chainId}/rate-plan-codes`,
			{
				...code,
				gds: 'amadeus',
				serviceType: 'hotel',
			}
		)

		return data
	} catch (error: any) {
		toast.error(error.response?.data?.message || 'Error al crear el código')
		throw error
	}
}

const useChainRatePlanCodes = (props: Props = {}) => {
	const toast = useToast()

	const { config, params } = props
	const { errorHandling = true } = config || {}
	const chainId = params?.chainId

	const { data, isLoading, isError, refetch, isRefetching } = useQuery(
		['chain-rate-plan-codes', chainId],
		() =>
			getRatePlanCodes(
				{
					chainId,
				},
				errorHandling
			),
		{ enabled: props.immediate && !!chainId }
	)

	const createRatePlanCodeMutation = useMutation(
		(code: CreateRatePlanCodeParams) => createRatePlanCode(code, toast)
	)

	return {
		ratePlanCodes: data,
		isLoading,
		isRefetching,
		isError,
		refetch,

		createRatePlanCode: createRatePlanCodeMutation.mutate,
		createRatePlanCodeAsync: createRatePlanCodeMutation.mutateAsync,
		createRatePlanCodeLoading: createRatePlanCodeMutation.isLoading,
	}
}

export default useChainRatePlanCodes
